import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMessages, saveMessage, resetUnreadCount } from '../../../utils/db';
import api from '../../../utils/api';

function ConversationWindow({ chat, onBack, socket, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    // Scroll the messages area so the input area at the bottom is visible
    const messagesArea = document.querySelector('.messages-area');
    if (messagesArea) {
      messagesArea.scrollTop = messagesArea.scrollHeight;
    }
  };

  // Load messages from IndexedDB and sync from backend when the chat opens
  useEffect(() => {
    const loadAndSyncMessages = async () => {
      setLoading(true);
      try {
        // Reset unread count when opening the conversation
        try {
          await resetUnreadCount(chat.id);
        } catch (err) {
          console.error('Failed to reset unread count:', err);
        }

        // First load local messages from IndexedDB
        const storedMessages = await getMessages(chat.id);
        setMessages(storedMessages);

        // Then sync any new messages from the backend (Firestore via API)
        setSyncing(true);
        const serverMessages = await api.getMessages(chat.id);

        if (serverMessages.length > storedMessages.length) {
          // Merge and deduplicate by id
          const existingIds = new Set(storedMessages.map(m => m.id));
          const newMessages = serverMessages.filter(m => !existingIds.has(m.id));

          if (newMessages.length > 0) {
            // Save new messages to IndexedDB
            const formattedMessages = newMessages.map(m => ({
              id: m.id,
              conversationId: m.conversation_id,
              text: m.text,
              sender: m.sender === 'me' ? 'me' : 'them',
              timestamp: m.created_at || Date.now(),
            }));

            // Save to IndexedDB
            for (const msg of formattedMessages) {
              await saveMessage(msg);
            }

            setMessages(prev => [...prev, ...formattedMessages]);
          }
        }
      } catch (err) {
        console.error('Failed to load messages:', err);
        setMessages([]);
      } finally {
        setLoading(false);
        setSyncing(false);
      }
    };
    loadAndSyncMessages();
  }, [chat.id]);

  // Scroll to bottom when messages change or on initial mount
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom on initial mount to ensure input area is visible
  useEffect(() => {
    // Small delay to ensure DOM has rendered
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, []);

  // Re-fetch messages from IndexedDB. Used after socket reconnect to pick up
  // any messages that were saved by Dashboard's background notification handler.
  const refreshMessages = useCallback(async () => {
    try {
      const storedMessages = await getMessages(chat.id);
      setMessages(storedMessages);
    } catch (err) {
      console.error('Failed to refresh messages:', err);
    }
  }, [chat?.id, setMessages]);

  // Join the conversation room when the chat opens, leave when it closes.
  // Also re-join the room when the socket reconnects (e.g., after a disconnect),
  // since Socket.IO does NOT automatically restore room membership on reconnect.
  useEffect(() => {
    if (!socket || !chat?.id) return;

    const room = chat.id;

    const handleConnect = () => {
      console.log(`🏠 Re-joining conversation room after reconnect: ${room}`);
      socket.emit('join_room', room);
      // Re-fetch messages from IndexedDB after socket reconnect to pick up
      // any pending messages that were saved by Dashboard's background handler
      // while the socket was disconnected (e.g., after mobile app minimize).
      refreshMessages();
    };

    // If already connected, join immediately
    if (socket.connected) {
      console.log(`🏠 Joining conversation room: ${room}`);
      socket.emit('join_room', room);
    }

    // Also join when the socket (re)connects
    socket.on('connect', handleConnect);

    return () => {
      console.log(`🚪 Leaving conversation room: ${room}`);
      socket.emit('leave_room', room);
      socket.off('connect', handleConnect);
    };
  }, [socket, chat?.id, refreshMessages]);

  // Handle incoming socket messages
  useEffect(() => {
    if (!socket) return;

    // Log all incoming socket events for debugging
    socket.onAny((event, ...args) => {
      console.log(`[Socket Event] ${event}`, args.length > 0 ? args[0] : '');
    });

    const handleNewMessage = async (data) => {
      const isFromOther = data.user_id !== (currentUser?.uid || 'react_user');

      if (isFromOther) {
        // Save incoming message to IndexedDB
        const savedMsg = await saveMessage({
          conversationId: chat.id,
          text: data.message,
          sender: 'them',
          timestamp: data.timestamp || Date.now(),
        });
        setMessages(prev => [...prev, savedMsg]);
      }
    };

    // Handle pending messages (rehydrated from Firestore)
    const handlePendingMessage = async (data) => {
      const msgText = data.text || '';
      const msgSender = data.sender || 'them';

      if (msgSender !== 'me') {
        const savedMsg = await saveMessage({
          conversationId: chat.id,
          text: msgText,
          sender: 'them',
          timestamp: data.created_at || Date.now(),
        });
        setMessages(prev => {
          if (prev.some(m => m.id === savedMsg.id)) return prev;
          return [...prev, savedMsg];
        });
      }
    };

    socket.on('new_message', handleNewMessage);
    socket.on('pending_message', handlePendingMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('pending_message', handlePendingMessage);
    };
  }, [socket, currentUser, chat.id]);

  const handleSend = async () => {
    if (!inputText.trim()) return;

    const text = inputText;
    setInputText('');

    // Save message to IndexedDB
    const savedMsg = await saveMessage({
      conversationId: chat.id,
      text,
      sender: 'me',
    });
    setMessages(prev => [...prev, savedMsg]);

    // Emit via socket if connected — the server handles Firestore persistence
    // for offline delivery. We do NOT call api.storeMessage() here because that
    // would create a duplicate entry in Firestore (the socket handler also stores it).
    if (socket && socket.connected) {
      socket.emit('chat_message', {
        user_id: currentUser?.uid || 'react_user',
        message: text,
        timestamp: Date.now(),
        conversation_id: chat.id,
      });
    } else {
      // Fallback: if socket is not connected, store via HTTP API instead
      try {
        const chatType = chat.id && chat.id.startsWith('invite_') ? 'anonymous' : 'my_chat';
        await api.storeMessage({
          conversationId: chat.id,
          text,
          senderId: currentUser?.uid || 'react_user',
          senderName: currentUser?.displayName || 'User',
          sender: 'me',
          chatType,
        });
      } catch (err) {
        console.error('Failed to store message on server:', err);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="conversation-container">
      <div className="conversation-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <img src={chat.avatar} alt={chat.name} className="chat-avatar" style={{width: 36, height: 36, marginRight: 12}} />
        <h3 className="chat-name" style={{margin: 0}}>{chat.name}</h3>
        {syncing && (
          <span style={{ marginLeft: 12, fontSize: 12, color: '#a0aab2' }}>
            Syncing...
          </span>
        )}
      </div>

      <div className="messages-area">
        {loading ? (
          <div style={{ textAlign: 'center', color: '#a0aab2', padding: 40 }}>
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#a0aab2', padding: 40 }}>
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`message-bubble ${msg.sender === 'me' ? 'sent' : 'received'}`}>
              {msg.text}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="message-input-area">
        <input
          type="text"
          className="message-input"
          placeholder="Type a message..."
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button className="send-btn" onClick={handleSend} disabled={!inputText.trim()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
        </button>
      </div>
    </div>
  );
}

export default ConversationWindow;
